import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Segmented, DatePicker, Input, Select } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, ArrowRightOutlined, UndoOutlined, SearchOutlined, PauseCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import { DashboardLayout } from '../layouts/DashboardLayout';
import { fetchNaverSyncLogs, rollbackPrices } from '../api/api';
import { NaverSyncLog, NaverSyncFieldType } from '../types/naverSyncLog';
import { FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS, COMPACT_LINK_STYLE, type ThemeTokens } from '../constants/constants';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { useIsCompact } from '../hooks/UseIsCompact';
import { useContentQuery } from '../hooks/UseContentWidth';
import { useJobRunner } from '../hooks/UseJobRunner';
import { useStorage } from '../contexts/StorageContext';
import { useCardPage } from '../contexts/CardPageContext';
import { AppButton } from '../components/common/AppButton';
import { AppTag } from '../components/common/AppTag';
import { AppPopconfirm } from '../components/common/AppPopconfirm';
import { AppLoadError } from '../components/common/AppLoadError';
import {
  SYNC_LOG_VIEW_LABEL, isResolvedFailure, resolvedBadgeLabel, syncLogResult, syncLogView, type SyncLogView,
} from '../utils/syncLogOutcome';
import { TableEmptyState, buildPagination, textColumn } from '../components/common/TableHelpers';
import { READ_ONLY_REASON } from '../constants/readOnly';
import { clearSyncLogFilter, readSyncLogFilter, type SyncLogOpenFilter } from '../utils/syncLogLink';
import type { RerunStep } from '../types/daily';

type Filter = 'ALL' | NaverSyncFieldType;

const FIELD_LABEL: Record<NaverSyncFieldType, string> = { PRICE: '가격', STOCK: '재고', SYNC: '동기화', REGISTER: '등록', HUB_PRICE: '매장 가격' };
// DETECT_MANUAL(2026-10-21) — 매장 관리자가 네이버에서 직접 바꾼 판매가를 감지한 기록(old=CT 기준가 · new=네이버 값).
// 감사 #532 — UPDATE_PRICES 는 ① '가격 받기'(옛 '시세 조회'), 코드 중복 정리 · 가격 상승 자동 반영 · 옛 매장 가격 요청은 라벨이 없어 영문으로 보였다.
export const JOB_LABEL: Record<string, string> = {
  SYNC_NAVER: '가격 반영', APPLY_STOCK: '재고 반영', SYNC_STOCK: '동기화', UPDATE_PRICES: '가격 받기', IMPORT_PRODUCTS: '신규 등록',
  REGISTER_PRODUCTS: '상품 등록', DETECT_MANUAL: '매장 직접 변경', HUB_REQUEST: '매장 가격 요청', DECIDED_PRICE: '판매가 결정',
  MANUAL_SELLER_CODE_FIX: '코드 중복 정리', SYNC_NAVER_AUTO: '가격 상승 자동 반영', APPLY_CURRENT_TO_HUB: '매장 가격 요청(옛)',
};

/** 반영 방식 필터 — 'ALL' 이면 전부. */
type JobFilter = 'ALL' | string;
/**
 * 결과 필터(감사 #538) — 건너뜀(확정 · 요청 대기)을 실패와 나눠 본다. 'RESOLVED'(백엔드 PR #95)는 실패였다가 다시 해서 성공한 줄 —
 * '실패' 는 남은 실패(outcome FAILED && !resolvedAt)만.
 */
type ResultFilter = 'ALL' | SyncLogView;

/** 재실행 한 번의 상한(백엔드 MorningChainService.RERUN_MAX_IDS). */
export const RERUN_MAX_IDS = 5_000;

/** 사람이 남긴 기록인가(로그인 username) — SCHEDULER · 옛 기록(null)은 아니다. */
const byPerson = (l: Pick<NaverSyncLog, 'actor'>) => !!l.actor && l.actor !== 'SCHEDULER';

/**
 * 남은 실패 줄 → 다시 돌릴 카드 id(유저 플로우 제안 1 · 백엔드 PR #95). 기록에는 카드 id 가 없어 판매자 상품코드로 카드 목록에서 찾는다
 * (코드 중복이면 그 코드의 카드 전부, 못 찾으면 unmatched). 해소된 줄(resolvedAt)은 넣지 않는다. exclude 에 걸린 줄은 excluded 로 센다.
 */
export function collectRetryTargets(
  rows: NaverSyncLog[], cardIdsByCode: Map<string, number[]>, exclude?: (l: NaverSyncLog) => boolean,
): { ids: number[]; unmatched: number; excluded: number } {
  const ids = new Set<number>();
  let unmatched = 0;
  let excluded = 0;
  rows.forEach((l) => {
    if (syncLogView(l) !== 'FAILED') return;
    if (exclude?.(l)) { excluded += 1; return; }
    const found = l.sellerProductCode ? cardIdsByCode.get(l.sellerProductCode) : undefined;
    if (found) found.forEach((id) => ids.add(id));
    else unmatched += 1;
  });
  return { ids: Array.from(ids), unmatched, excluded };
}

/**
 * 지금 보기에서 '실패 N건 다시 돌리기'(백엔드 PR #95)가 다시 돌릴 아침 단계 — 남은 실패 보기일 때만. 동기화(SYNC_STOCK)는 반영 방식만으로,
 * 상승분 자동 반영은 카드별 실패가 ③ 과 같은 SYNC_NAVER 라 아침 카드에서 걸어 온 보기(힌트 step AUTO_INCREASE)일 때만. 가격 받기는
 * 예전 '실패 N건 가격 다시 받기'(POST /update-prices) 그대로라 여기 없다. 옛 서버(404)를 한 번 만났으면 없음(이동만).
 */
export function rerunStepFor(
  jobFilter: string, resultFilter: string, hintStep: string | null | undefined, hintActive: boolean, supported: boolean | null,
): RerunStep | null {
  if (resultFilter !== 'FAILED' || supported === false) return null;
  if (jobFilter === 'SYNC_STOCK') return 'SYNC_STOCK';
  if (jobFilter === 'SYNC_NAVER' && hintActive && hintStep === 'AUTO_INCREASE') return 'AUTO_INCREASE';
  return null;
}

/** actor 표시 — SCHEDULER 는 "자동", 옛 기록(null)은 "-". */
const actorLabel = (v: string | null | undefined) => (v == null || v === '' ? '-' : v === 'SCHEDULER' ? '자동(스케줄러)' : v);

/**
 * 1000~1279px — 사이드바(220px)가 아직 남아 본문이 732~1,011px 로 좁아지는 구간.
 * 이 구간에서는 오른쪽 고정 열(결과 150 + 비고 240 = 390px)이 본문의 절반 이상을 차지해
 * 가운데 열이 거의 안 보이므로 고정을 끄고, 열 폭도 줄인다(2026-10-20 PR #37 리뷰).
 */
const NARROW_TABLE_QUERY = '(max-width: 1279px)';
/**
 * 1280~1439px — 본문이 약 1,012~1,171px. 결과 · 비고 둘을 고정하면(약 440px, 선택 칸 포함) 가운데의 변경 내역 ·
 * 반영 방식 · 실행자가 고정 열 밑에 깔려 1280 에서 안 보였다(2026-10-21 코덱 화면 점검, 감사 card-controltower#62).
 * 이 구간은 '결과' 하나만 고정하고 열 폭을 줄인다 — 성공 · 실패는 늘 보이고, 비고는 가로 스크롤 끝에서 본다.
 */
const MID_TABLE_QUERY = '(max-width: 1439px)';
/**
 * 열 세트는 본문 폭으로 고른다(디자인 시스템 v2 · CT 배치 규칙 L3b — 본문 최대 폭 1440 이 생겨 화면 폭 질의가 틀린다): 넓은 세트 합
 * 1,548 이 들어가면 넓은(결과 · 비고 고정), 중간 세트 합 1,286 이 들어가면 중간(결과만), 그 밖은 좁은(고정 없음). 위 두 화면 폭 질의는
 * 본문을 재기 전(첫 그리기 · 본문 상자 없는 단위 테스트)에만 쓴다.
 */
const WIDE_SET_MIN = 1548;
const MID_SET_MIN = 1286;


const fmtValue = (v: number | null, field: NaverSyncFieldType) =>
  v == null ? '-' : field === 'PRICE' || field === 'HUB_PRICE' ? `${v.toLocaleString()}원` : `${v.toLocaleString()}개`;

/** 가격 롤백 대상이 될 수 있는 기록인가 — 실제로 반영된(dry-run 아닌) 성공한 가격 변경만. */
const isRollbackEligible = (l: NaverSyncLog) => l.fieldType === 'PRICE' && l.success && !l.dryRun;

// ─── 컬럼 정의 ───────────────────────────────────────────────────────────────
// fixedRight: 데스크톱에서 '결과'·'비고(실패 사유)'를 오른쪽에 고정한다 — 1280 화면에서는
// 표 최소 폭(1,380px)이 본문(약 1,012px)보다 넓어, 고정하지 않으면 정작 알아야 할
// 성공/실패·사유가 가로 스크롤 뒤에 숨는다(2026-10-20 감사 "화면 · 표").
// 좁은 폭(Drawer 모드)에서는 고정 열 두 개가 화면 폭을 다 먹어 본문이 안 보이므로 끈다.
// narrow(1000~1279px): 사이드바(220px)가 남아 본문이 732~1,011px 로 좁다 — 고정 열 390px 가
// 절반 이상을 차지하고, 표 최소 폭(1,380px)도 너무 넓어 열 폭을 줄인다(합 1,138px).
// 2026-10-20 PR #37 리뷰.
// fixedRight='result'(1280~1439px): '결과' 만 고정한다 — 위 MID_TABLE_QUERY 설명.
export function buildColumns(t: ThemeTokens, fixedRight: boolean | 'result', narrow = false): ColumnsType<NaverSyncLog> {
  const pin: 'right' | undefined = fixedRight ? 'right' : undefined;
  const pinNote: 'right' | undefined = fixedRight === true ? 'right' : undefined;
  const w = (wide: number, narrowWidth: number) => (narrow ? narrowWidth : wide);
  return [
    textColumn<NaverSyncLog>(t, '반영일시', 'syncedAt', {
      width: w(160, 140),
      defaultSortOrder: 'descend',
      sorter: (a, b) => dayjs(a.syncedAt).valueOf() - dayjs(b.syncedAt).valueOf(),
      color: t.textSecondary,
      format: (v) => dayjs(v).format('YYYY/MM/DD HH:mm:ss'),
    }),
    textColumn<NaverSyncLog>(t, '구분', 'fieldType', {
      width: w(90, 76),
      format: (v: NaverSyncFieldType) => FIELD_LABEL[v] ?? v,
    }),
    textColumn<NaverSyncLog>(t, '상품명', 'productName', { width: w(260, 200), bold: true }),
    textColumn<NaverSyncLog>(t, '상품코드', 'sellerProductCode', {
      width: w(150, 120), fontSize: FONT_SIZE.xs, color: t.textMuted,
    }),
    {
      title: '변경 내역', width: w(220, 180),
      render: (_: unknown, r: NaverSyncLog) => (
        r.fieldType === 'SYNC' ? (
          // SYNC(동기화 조회 실패)는 반영 전후 값 개념이 없다 — "실패 사유" 컬럼이 대신함.
          <span style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>-</span>
        ) : (
          <div
            // 판매가 결정(감사 #340 · #341) — 앞 값은 PUT 직전 네이버 값, 뒤 값은 정한 판매가.
            title={r.jobType === 'DECIDED_PRICE' ? '직전 네이버가 → 결정가' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, fontSize: FONT_SIZE.sm }}
          >
            <span style={{ color: t.textMuted }}>{fmtValue(r.oldValue, r.fieldType)}</span>
            <ArrowRightOutlined style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }} />
            <span style={{ color: t.textPrimary, fontWeight: FONT_WEIGHT.semibold }}>{fmtValue(r.newValue, r.fieldType)}</span>
          </div>
        )
      ),
    },
    textColumn<NaverSyncLog>(t, '반영 방식', 'jobType', {
      width: w(110, 92),
      color: (v) => (v === 'DETECT_MANUAL' ? t.warning : t.textSecondary),
      format: (v) => JOB_LABEL[v] ?? v,
    }),
    // 누가 반영했나(2026-10-21) — 로그인 username 또는 SCHEDULER. 그 전 기록은 "-".
    textColumn<NaverSyncLog>(t, '실행자', 'actor', {
      width: w(120, 100), color: (v) => (v ? t.textSecondary : t.textEmpty),
      format: (v) => actorLabel(v),
    }),
    {
      title: '결과', dataIndex: 'success', width: w(150, 130), fixed: pin,
      render: (v: boolean, r: NaverSyncLog) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs }}>
          {/* 건너뜀(확정 · 요청 대기라 보내지 않음, 백엔드 PR #76)은 실패(빨강)가 아니라 회색 — 감사 #538 */}
          {syncLogResult(r) === 'SKIPPED' ? (
            <span
              data-testid="sync-log-skipped"
              title={r.errorMessage ?? '확정 대기 · 요청 대기라 보내지 않았습니다(실패 아님)'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold,
                color: t.textSecondary, background: t.bgRaised, border: `1px solid ${t.border}`,
                padding: `0 ${SPACING.xxs}px`, borderRadius: RADIUS.sm, whiteSpace: 'nowrap',
              }}
            >
              <PauseCircleOutlined aria-hidden /> 건너뜀
            </span>
          ) : <AppTag variant="syncResult" success={v} errorMessage={r.errorMessage} />}
          {/* 실패였다가 같은 날 다시 해서 성공(백엔드 PR #95 resolvedAt) — 남은 실패에서 뺀 줄 */}
          {isResolvedFailure(r) && (
            <span
              data-testid="sync-log-resolved"
              title={`${dayjs(r.resolvedAt as string).format('MM/DD HH:mm')} 다시 해서 성공 — 남은 실패에서 뺐습니다`}
              style={{
                fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold, whiteSpace: 'nowrap',
                color: t.success, background: t.tintSuccess, border: `1px solid ${t.lineSuccess}`,
                padding: `0 ${SPACING.xxs}px`, borderRadius: RADIUS.sm,
              }}
            >
              {resolvedBadgeLabel(r.jobType)}
            </span>
          )}
          {r.dryRun && (
            <span
              title="write-enabled=false(로컬 등) 또는 허용목록 미포함으로 실제 네이버 반영 없이 기록만 남음"
              style={{
                fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold,
                color: t.warning, background: t.tintWarning,
                padding: `0 ${SPACING.xxs}px`, borderRadius: RADIUS.sm,
              }}
            >
              DRY-RUN
            </span>
          )}
        </div>
      ),
    },
    // 실패면 사유(빨강), 성공이지만 메모가 있으면(예: "스토어 실제 재고 N개였으므로 최종
    // M개 반영") 안내로 표시(회색) — 성공 행에 빨간 사유가 뜨면 오해를 부른다.
    // 고정 열은 폭이 있어야 sticky 계산이 되므로 240px 을 준다.
    textColumn<NaverSyncLog>(t, '비고', 'errorMessage', {
      width: w(240, 200), fixed: pinNote,
      ellipsis: true, fontSize: FONT_SIZE.xs,
      // 해소된 실패(재수집 · 재반영 성공)의 사유는 지난 일이라 회색.
      color: (v, r) => (v && !r.success && syncLogView(r) === 'FAILED' ? t.danger : t.textMuted),
      format: (v) => v ?? '-',
      cellTitle: (v) => v ?? undefined,
    }),
  ];
}

/**
 * 아침 카드에서 걸어 온 보기(힌트)가 아직 그대로인가 — 반영 방식 · 결과 · 날짜가 힌트와 같을 때만 안내 줄에 아침 카드 라벨을 붙인다.
 * 다른 날짜를 조회하면 거둔다(감사 card-controltower#798, 코덱 #507 낮음). 힌트에 날짜가 없으면 오늘.
 */
export function syncLogHintActive(
  hint: SyncLogOpenFilter | null, jobFilter: string, resultFilter: string, date: Dayjs | null,
): boolean {
  if (!hint || jobFilter !== hint.jobType || resultFilter !== 'FAILED' || !date) return false;
  return date.format('YYYY-MM-DD') === (hint.date ?? dayjs().format('YYYY-MM-DD'));
}

// ─── NaverSyncLogPage ─────────────────────────────────────────────────────────

/**
 * "반영 로그" — 실제 Naver 스마트스토어로 가격·재고가 PUT된 기록을 시간순으로 보여준다.
 * 언제 어떤 카드가 얼마→얼마로 바뀌었는지 추적할 방법이 없었던 것을 사용자가 요청해 신설
 * (2026-07-29). 백엔드가 반영 시도마다(성공·실패 모두) 기록을 남기고, 여기선 조회만 한다 —
 * BuybackPage와 같은 "컨텍스트 프로바이더 없이 로컬 상태로 조회만 하는" 단순 페이지 패턴.
 *
 * 가격 롤백(2026-08-04)은 검색 + 체크박스 선택 기반이다 — 광주점이 하루치 전체가 아니라
 * "디지몬카드만" 처럼 일부만 되돌리고 싶어해서, 날짜 단위 일괄 롤백 대신 상품명/코드로
 * 검색해 원하는 행만 골라 롤백할 수 있게 했다. 실제로 반영된(dry-run 아닌) 성공한 가격
 * 변경만 체크 가능하다(isRollbackEligible) — 재고/동기화/실패/dry-run 행은 되돌릴 대상
 * 자체가 없어 체크박스를 비활성화한다.
 */
export const NaverSyncLogPage: React.FC = () => {
  const t = useThemeTokens();
  const { isAdmin } = useStorage();
  const { jobs, cards } = useCardPage();
  // 아침 카드의 "실패 N건 보기" 가 남긴 한 번짜리 힌트(유저 플로우 제안 1) — 그날 · 그 반영 방식 · 실패만으로 연다. 마운트 뒤 지운다.
  const [openHint, setOpenHint] = useState<SyncLogOpenFilter | null>(() => readSyncLogFilter());
  useEffect(() => { clearSyncLogFilter(); }, []);
  // 좁은 폭(Drawer 모드)에서는 오른쪽 고정 열을 끈다 — 고정 열(150+240px)이 화면 폭을
  // 거의 다 차지해 나머지 열이 안 보인다(2026-10-20 감사 "화면 · 표"). 사이드바가 남는
  // 1000~1279px 도 본문(732~1,011px)에 비해 고정 열 390px 가 너무 커서 같이 끈다
  // (2026-10-20 PR #37 리뷰) — 이 구간에서는 열 폭을 줄인 컬럼을 쓴다.
  const compact = useIsCompact();
  const narrowTable = useContentQuery((w) => w < MID_SET_MIN, NARROW_TABLE_QUERY);
  const midTable = useContentQuery((w) => w < WIDE_SET_MIN, MID_TABLE_QUERY);
  // 본문이 열 세트 합보다 좁아도(1280 · 1440 의 본문 1,012 · 1,172 가 '좁은' 세트로 떨어져 '결과' 고정이 풀렸다 — 시우 검수 M1) 결과 열(130px)은 늘 고정한다.
  // 열 하나라 본문이 가려지지 않는다. 고정을 끄는 것은 Drawer 폭(compact)뿐.
  const pinRight: boolean | 'result' = compact ? false : narrowTable || midTable ? 'result' : true;
  const [data, setData]         = useState<NaverSyncLog[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<Error | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [filter, setFilter]     = useState<Filter>('ALL');
  const [jobFilter, setJobFilter] = useState<JobFilter>(() => openHint?.jobType ?? 'ALL');
  const [resultFilter, setResultFilter] = useState<ResultFilter>(() => (openHint ? 'FAILED' : 'ALL'));
  // null = 날짜 필터 없음(최근 10000건, 기존 동작). 값이 있으면 그 하루만 조회.
  const [date, setDate]         = useState<Dayjs | null>(() => {
    if (!openHint) return null;
    const d = dayjs(openHint.date ?? undefined);
    return d.isValid() ? d.startOf('day') : null;
  });
  const [search, setSearch]     = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [rollingBack, setRollingBack] = useState(false);

  // ── 검색 디바운스 (StockToolbar 의 검색과 같은 패턴) ────────────
  // setSearch가 곧바로 filtered 재계산(최대 1만 건 순회)을 유발하므로, 입력 자체는
  // 즉시 반영하되 실제 필터 갱신은 타이핑이 멈춘 뒤 300ms 후에만 한다.
  const [searchDraft, setSearchDraft] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchDraft(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 300);
  }, []);

  useEffect(() => { setSearchDraft(search); }, [search]);

  // 목록 조회 경쟁 방지: 마지막에 시작한 조회의 응답만 반영한다(늦게 도착한 옛 응답 무시).
  const reqSeq = useRef(0);
  const load = useCallback(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    fetchNaverSyncLogs(date ? date.format('YYYY-MM-DD') : undefined)
      .then((res) => { if (seq === reqSeq.current) setData(res); })
      .catch((e: unknown) => {
        if (seq === reqSeq.current) {
          // 원인(백엔드 오류 메시지·네트워크 오류)을 버리지 않고 그대로 넘긴다 — 예전엔
          // 고정 문구로 덮어써 AppLoadError 에 왜 실패했는지가 안 보였다(2026-10-19 리뷰).
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [date]);

  useEffect(() => { load(); }, [load]);
  // 조회 조건이 바뀌면(날짜 등) 이전 선택은 더 이상 화면에 없는 행을 가리킬 수 있어 비운다.
  useEffect(() => { setSelectedIds([]); }, [date]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: data.length, PRICE: 0, STOCK: 0, SYNC: 0 };
    data.forEach((l) => { c[l.fieldType] = (c[l.fieldType] ?? 0) + 1; });
    return c;
  }, [data]);

  const segmentOptions = useMemo(
    () => [
      { label: `전체 ${counts.ALL}`, value: 'ALL' as Filter },
      { label: `가격 ${counts.PRICE}`, value: 'PRICE' as Filter },
      { label: `재고 ${counts.STOCK}`, value: 'STOCK' as Filter },
      { label: `동기화 ${counts.SYNC}`, value: 'SYNC' as Filter },
      // 등록 · 매장 가격 기록은 받은 것이 있을 때만(감사 #532 — 필터가 없어 '전체' 에서만 보였다).
      ...((counts.REGISTER ?? 0) > 0 ? [{ label: `${FIELD_LABEL.REGISTER} ${counts.REGISTER}`, value: 'REGISTER' as Filter }] : []),
      ...((counts.HUB_PRICE ?? 0) > 0 ? [{ label: `${FIELD_LABEL.HUB_PRICE} ${counts.HUB_PRICE}`, value: 'HUB_PRICE' as Filter }] : []),
    ],
    [counts],
  );

  // 반영 방식 옵션 — 지금 받은 기록에 있는 종류만(건수 함께).
  const jobOptions = useMemo(() => {
    const c = new Map<string, number>();
    data.forEach((l) => c.set(l.jobType, (c.get(l.jobType) ?? 0) + 1));
    // 아침 카드에서 걸어 온 반영 방식이 받은 기록에 없으면(그날 실패 기록 0건 등) 0 으로라도 보인다 — 영문 값이 그대로 보이지 않게.
    if (jobFilter !== 'ALL' && !c.has(jobFilter)) c.set(jobFilter, 0);
    return [
      { label: `반영 방식 전체 ${data.length}`, value: 'ALL' },
      ...Array.from(c.entries()).map(([k, n]) => ({ label: `${JOB_LABEL[k] ?? k} ${n}`, value: k })),
    ];
  }, [data, jobFilter]);

  // 결과 옵션 — 지금 받은 기록의 성공 · 남은 실패 · 건너뜀 · 재시도 성공 건수(건너뜀 · 재시도 성공은 있을 때만).
  const resultOptions = useMemo(() => {
    const c: Record<SyncLogView, number> = { SUCCESS: 0, SKIPPED: 0, FAILED: 0, RESOLVED: 0 };
    data.forEach((l) => { c[syncLogView(l)] += 1; });
    return [
      { label: `결과 전체 ${data.length}`, value: 'ALL' },
      ...(['SUCCESS', 'FAILED', 'RESOLVED', 'SKIPPED'] as SyncLogView[])
        .filter((k) => (k !== 'SKIPPED' && k !== 'RESOLVED') || c[k] > 0)
        .map((k) => ({ label: `${SYNC_LOG_VIEW_LABEL[k]} ${c[k]}`, value: k })),
    ];
  }, [data]);
  // 새 서버(백엔드 PR #95)는 줄마다 resolvedAt 칸을 준다(null 포함) — 옛 서버면 다시 해서 성공해도 실패 줄이 그대로 남는다.
  const resolvedKnown = useMemo(() => data.some((l) => l.resolvedAt !== undefined), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((l) => {
      if (filter !== 'ALL' && l.fieldType !== filter) return false;
      if (jobFilter !== 'ALL' && l.jobType !== jobFilter) return false;
      // '실패' 는 남은 실패만 — 해소된 줄(resolvedAt)은 '재시도 성공' 으로(백엔드 PR #95 계약 outcome FAILED && !resolvedAt).
      if (resultFilter !== 'ALL' && syncLogView(l) !== resultFilter) return false;
      if (q && !l.productName.toLowerCase().includes(q) && !l.sellerProductCode.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, filter, jobFilter, resultFilter, search]);

  // 아침 카드에서 걸어 온 보기가 아직 그대로인가 — 필터(날짜 포함 — 감사 card-controltower#798)를 바꾸면 안내 줄을 거둔다.
  const hintActive = syncLogHintActive(openHint, jobFilter, resultFilter, date);
  const clearHint = useCallback(() => {
    setOpenHint(null);
    setJobFilter('ALL');
    setResultFilter('ALL');
  }, []);

  // 가격 받기 실패 다시 받기(유저 플로우 제안 1) — 기존 POST /api/cards/update-prices(ids)를 지금 보이는 실패 줄의 카드로만.
  // 기록에는 카드 id 가 없어 판매자 상품코드로 카드 목록에서 찾는다(코드 중복이면 그 코드의 카드 전부, 못 찾으면 뺀다).
  const cardIdsByCode = useMemo(() => {
    const m = new Map<string, number[]>();
    cards.cards.forEach((c) => {
      if (!c.sellerProductCode) return;
      const list = m.get(c.sellerProductCode);
      if (list) list.push(c.id); else m.set(c.sellerProductCode, [c.id]);
    });
    return m;
  }, [cards.cards]);
  const retryTargets = useMemo(() => {
    if (jobFilter !== 'UPDATE_PRICES' || resultFilter !== 'FAILED') return null;
    return collectRetryTargets(filtered, cardIdsByCode);
  }, [jobFilter, resultFilter, filtered, cardIdsByCode]);
  const retryBusy = !!jobs.activeJobId || jobs.priceUpdating;
  const retryDisabledReason = !isAdmin ? READ_ONLY_REASON
    : !retryTargets || retryTargets.ids.length === 0 ? '다시 받을 카드가 없습니다(카드 목록에서 상품코드를 찾지 못함)'
    : retryBusy ? '다른 작업이 진행 중입니다'
    : undefined;

  // 다시 받기 · 다시 돌리기 잡이 끝나면 기록을 다시 받는다 — 풀린 실패 줄이 '재수집 성공' 으로 바뀌어 남은 실패에서 빠지게(백엔드 PR #95).
  const reloadAfterJobRef = useRef(false);
  const sawJobRef = useRef(false);
  // 시작 응답 뒤(await 다음)에는 진행바 잡 id 가 이미 그려졌을 수도, 아직일 수도 있다 — 렌더 때 값을 거울로 둬 둘 다 받는다.
  const activeJobIdRef = useRef(jobs.activeJobId);
  activeJobIdRef.current = jobs.activeJobId;
  useEffect(() => {
    if (!reloadAfterJobRef.current) return;
    if (jobs.activeJobId) { sawJobRef.current = true; return; }
    if (!sawJobRef.current) return;
    reloadAfterJobRef.current = false;
    sawJobRef.current = false;
    load();
  }, [jobs.activeJobId, load]);
  const expectReloadAfterJob = useCallback(() => {
    reloadAfterJobRef.current = true;
    sawJobRef.current = activeJobIdRef.current != null;
  }, []);

  const retryInFlightRef = useRef(false);
  const retryFailedPrices = useCallback(async () => {
    if (!retryTargets || retryTargets.ids.length === 0 || retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    try {
      const started = await jobs.startUpdatePrices(retryTargets.ids, { partial: true });
      if (started) {
        expectReloadAfterJob();
        message.success(`실패 ${retryTargets.ids.length.toLocaleString()}건 가격을 다시 받습니다. 상단 진행바에서 확인하세요.`);
      }
    } finally {
      retryInFlightRef.current = false;
    }
  }, [retryTargets, jobs, expectReloadAfterJob]);

  // 동기화 · 상승분 자동 반영 실패 다시 돌리기(백엔드 PR #95 POST /morning-chain/rerun, 감사 card-controltower#796).
  const rerunStep = rerunStepFor(jobFilter, resultFilter, openHint?.step, hintActive, jobs.rerunSupported);
  const rerunTargets = useMemo(() => {
    if (!rerunStep) return null;
    // 상승분 자동 반영 — 사람이 보낸 ③ 스토어 반영 실패(같은 SYNC_NAVER)는 자동 반영으로 다시 올리지 않는다.
    return collectRetryTargets(filtered, cardIdsByCode, rerunStep === 'AUTO_INCREASE' ? byPerson : undefined);
  }, [rerunStep, filtered, cardIdsByCode]);
  const [rerunStarting, setRerunStarting] = useState(false);
  const rerunDisabledReason = !isAdmin ? READ_ONLY_REASON
    : !rerunTargets || rerunTargets.ids.length === 0 ? '다시 돌릴 카드가 없습니다(카드 목록에서 상품코드를 찾지 못함)'
    : rerunTargets.ids.length > RERUN_MAX_IDS ? `한 번에 ${RERUN_MAX_IDS.toLocaleString()}장까지 다시 돌릴 수 있습니다`
    : retryBusy ? '다른 작업이 진행 중입니다'
    : undefined;
  const rerunInFlightRef = useRef(false);
  const rerunFailed = useCallback(async () => {
    if (!rerunStep || !rerunTargets || rerunTargets.ids.length === 0 || rerunTargets.ids.length > RERUN_MAX_IDS) return;
    if (rerunInFlightRef.current) return;
    rerunInFlightRef.current = true;
    setRerunStarting(true);
    try {
      const r = await jobs.startRerun(rerunStep, rerunTargets.ids);
      if (r === 'started') {
        expectReloadAfterJob();
        message.success(`실패 ${rerunTargets.ids.length.toLocaleString()}건을 다시 돌립니다. 상단 진행바에서 확인하세요.`);
      }
    } finally {
      rerunInFlightRef.current = false;
      setRerunStarting(false);
    }
  }, [rerunStep, rerunTargets, jobs, expectReloadAfterJob]);

  const eligibleFilteredIds = useMemo(
    () => filtered.filter(isRollbackEligible).map((l) => l.id),
    [filtered],
  );
  const allFilteredSelected = eligibleFilteredIds.length > 0
    && eligibleFilteredIds.every((id) => selectedIds.includes(id));

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys: React.Key[]) => setSelectedIds(keys as number[]),
    getCheckboxProps: (record: NaverSyncLog) => ({ disabled: !isRollbackEligible(record) }),
    // 오른쪽 열(결과·비고)을 고정했으니 선택 체크박스는 왼쪽에 고정한다 — 가로 스크롤 중에도
    // 어떤 행을 골랐는지 보여야 롤백 선택이 어긋나지 않는다(2026-10-20 PR #37 리뷰).
    fixed: 'left' as const,
    columnWidth: 48,
  };

  const runJob = useJobRunner(jobs.setActiveJobId);

  // 이 호출이 직접 롤백 시작 요청을 진행 중인지 나타내는 표식(2026-10-19 리뷰 #2). runJob 의
  // in-flight 가드로 막힌 연타 호출은 false 를 받는데, 그 false 로 setRollingBack(false) 를 하면
  // 첫 요청의 로딩 표시가 먼저 꺼진다 — 막힌 호출은 시작 전에 돌려보낸다.
  const rollbackInFlightRef = useRef(false);

  const handleRollback = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (rollbackInFlightRef.current) return;
    rollbackInFlightRef.current = true;
    setRollingBack(true);
    // useJobRunner 의 inFlightRef 로 응답을 기다리는 구간까지 연타를 막는다(2026-10-19
    // 리뷰 #5). rollingBack state 만으로는 Popconfirm 확정이 닫힌 뒤 두 번째 클릭이 들어와
    // 같은 선택 건으로 롤백 잡이 두 번 만들어질 수 있었다.
    try {
      const started = await runJob(
        () => rollbackPrices(selectedIds),
        '가격 롤백 시작 중 오류가 발생했습니다.',
        () => setSelectedIds([]),
        // 롤백도 ③ 과 같은 게이트 — 확정 대기 1,000건 잘림이면 409(백엔드 PR #82, 감사 #700).
        { conflictLabel: '반영 막힘' },
      );
      if (started) message.success('가격 롤백을 시작했습니다. 상단 진행바에서 확인하세요.');
    } finally {
      rollbackInFlightRef.current = false;
      setRollingBack(false);
    }
  }, [selectedIds, runJob]);

  return (
    <DashboardLayout title="반영 로그">
      <div style={{ marginBottom: SPACING.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.sm, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
          <Segmented options={segmentOptions} value={filter} onChange={(v) => setFilter(v as Filter)} />
          <Select
            aria-label="반영 방식"
            value={jobFilter}
            onChange={(v) => setJobFilter(v)}
            options={jobOptions}
            style={{ minWidth: 160 }}
            popupMatchSelectWidth={false}
          />
          <Select
            aria-label="결과"
            value={resultFilter}
            onChange={(v) => setResultFilter(v as ResultFilter)}
            options={resultOptions}
            style={{ minWidth: 120 }}
            popupMatchSelectWidth={false}
          />
          <DatePicker
            value={date}
            onChange={setDate}
            placeholder="날짜 선택"
            allowClear
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
          />
          <Input.Search
            placeholder="상품명·상품코드 검색"
            prefix={<SearchOutlined />}
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
        </div>
        <AppButton size={compact ? 'lg' : 'md'} icon={<ReloadOutlined />} onClick={load} loading={loading}>새로고침</AppButton>
      </div>

      {(hintActive || retryTargets || rerunTargets) && (
        <div data-testid="sync-log-failures" role="note" style={{
          display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
          marginBottom: SPACING.sm, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          background: t.tintDanger, border: `1px solid ${t.lineDanger}`, borderRadius: RADIUS.md,
          fontSize: FONT_SIZE.sm, color: t.textPrimary,
        }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            {hintActive && openHint?.label ? `${openHint.label} — ` : ''}
            {`실패 ${filtered.length.toLocaleString()}건`}
            {date ? ` · ${date.format('YYYY/MM/DD')}` : ''}
            <span style={{ display: 'block', fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
              {resolvedKnown
                // 새 서버(백엔드 PR #95) — 다시 해서 성공한 줄은 resolvedAt 으로 풀려 이 수에서 빠진다.
                ? '같은 카드 · 같은 사유의 실패는 하루 한 줄이라 아침 카드의 수와 다를 수 있습니다. 다시 해서 성공한 줄은 \'재수집 · 재반영 성공\' 으로 바뀌어 이 수에서 빠집니다(결과 \'재시도 성공\' 으로 보기).'
                : '같은 카드 · 같은 사유의 실패는 하루 한 줄이라 아침 카드의 수와 다를 수 있고, 다시 받아 성공해도 이 줄은 남습니다(결과는 가격 조회 화면에서).'}
              {retryTargets && retryTargets.unmatched > 0
                ? ` 상품코드로 카드를 찾지 못한 ${retryTargets.unmatched.toLocaleString()}건은 다시 받기에서 빠집니다.`
                : ''}
              {rerunTargets && rerunTargets.unmatched > 0
                ? ` 상품코드로 카드를 찾지 못한 ${rerunTargets.unmatched.toLocaleString()}건은 다시 돌리기에서 빠집니다.`
                : ''}
              {rerunTargets && rerunTargets.excluded > 0
                ? ` 사람이 보낸 ③ 스토어 반영 실패 ${rerunTargets.excluded.toLocaleString()}건은 자동 반영으로 다시 돌리지 않습니다(③ 에서 다시).`
                : ''}
              {rerunStep === 'AUTO_INCREASE'
                ? ' 상승분 자동 반영은 조건(신선도 · 상승률 상한 · 출처 재조회)을 다시 보고 통과한 카드만 네이버 가격을 올립니다.'
                : ''}
            </span>
            {/* 터치 기기는 비활성 버튼 툴팁이 잘 안 뜬다 — 잠금 이유를 글로(시우 검수 PR #93 낮음). */}
            {(retryTargets || rerunTargets) && !isAdmin && (
              <span data-testid="sync-log-readonly" style={{ display: 'block', fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
                {`${READ_ONLY_REASON} — 다시 받기 · 다시 돌리기는 관리자가 합니다`}
              </span>
            )}
          </span>
          {retryTargets && (
            <AppButton
              size={compact ? 'lg' : 'sm'} variant="primary" icon={<ReloadOutlined />}
              data-testid="sync-log-retry-prices"
              loading={jobs.priceUpdating}
              disabled={!!retryDisabledReason}
              disabledReason={retryDisabledReason}
              onClick={() => { void retryFailedPrices(); }}
            >
              {`실패 ${(retryTargets.ids.length).toLocaleString()}건 가격 다시 받기`}
            </AppButton>
          )}
          {rerunStep && rerunTargets && (() => {
            const label = `실패 ${rerunTargets.ids.length.toLocaleString()}건 다시 돌리기`;
            const button = (
              <AppButton
                size={compact ? 'lg' : 'sm'} variant="primary" icon={<ReloadOutlined />}
                data-testid="sync-log-rerun"
                loading={rerunStarting}
                disabled={!!rerunDisabledReason}
                disabledReason={rerunDisabledReason}
                onClick={rerunStep === 'AUTO_INCREASE' ? undefined : () => { void rerunFailed(); }}
              >
                {label}
              </AppButton>
            );
            // 상승분 자동 반영은 네이버 판매가를 실제로 올린다 — 한 번 더 묻는다(동기화는 읽기라 바로).
            return rerunStep === 'AUTO_INCREASE' && !rerunDisabledReason ? (
              <AppPopconfirm
                title="상승분 자동 반영 다시 돌리기"
                description={`실패 ${rerunTargets.ids.length.toLocaleString()}건을 자동 반영 조건으로 다시 보고, 통과한 카드만 네이버 판매가를 올립니다. 계속할까요?`}
                okText="다시 돌리기"
                onConfirm={() => { void rerunFailed(); }}
              >
                {button}
              </AppPopconfirm>
            ) : button;
          })()}
          {hintActive && (
            // 휴대폰 · 좁은 폭은 누르는 곳 44px(시우 검수 PR #93 중간 — 390 에서 17px 였다).
            <AppButton variant="link" style={compact ? { ...COMPACT_LINK_STYLE, height: 44, minHeight: 44 } : COMPACT_LINK_STYLE}
              onClick={clearHint}>필터 지우기</AppButton>
          )}
        </div>
      )}

      {isAdmin && eligibleFilteredIds.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.sm,
          marginBottom: SPACING.sm, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          background: t.tintInfo, border: `1px solid ${t.lineAccent}`, borderRadius: RADIUS.md,
        }}>
          <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>
            {selectedIds.length > 0 ? `${selectedIds.length.toLocaleString()}건 선택됨` : '가격 롤백 대상 선택'}
          </span>
          <AppButton
            variant="link"
            style={COMPACT_LINK_STYLE}
            onClick={() => setSelectedIds(allFilteredSelected ? [] : eligibleFilteredIds)}
          >
            {allFilteredSelected ? '선택 해제' : `검색 결과 ${eligibleFilteredIds.length.toLocaleString()}건 전체 선택`}
          </AppButton>
          <div style={{ flex: 1 }} />
          {selectedIds.length > 0 && (
            <AppPopconfirm
              title="선택 항목 가격 롤백"
              description={`선택한 ${selectedIds.length.toLocaleString()}건을 전부 반영 이전 값으로 되돌려 다시 Naver에 반영합니다. 되돌리면 그 결과도 새 반영 기록으로 남고, 이 작업 자체를 취소할 수는 없습니다. 계속할까요?`}
              okText="롤백 실행"
              danger
              onConfirm={handleRollback}
            >
              <AppButton
                variant="ghost-danger"
                icon={<UndoOutlined />}
                loading={rollingBack || jobs.naverSyncing}
                disabled={!!jobs.activeJobId}
                disabledReason="다른 작업이 진행 중입니다"
              >
                선택 가격 롤백 ({selectedIds.length.toLocaleString()})
              </AppButton>
            </AppPopconfirm>
          )}
        </div>
      )}

      {error ? (
        <AppLoadError message={error.message} error={error} onRetry={load} />
      ) : (
        <Table
          rowKey="id"
          columns={buildColumns(t, pinRight, narrowTable || midTable)}
          dataSource={filtered}
          rowSelection={isAdmin ? rowSelection : undefined}
          rowClassName={(r) => r.dryRun ? 'row-dry-run' : ''}
          loading={loading}
          bordered
          pagination={buildPagination(pageSize, setPageSize)}
          scroll={{ x: 'max-content' }}
          size="small"
          locale={{
            emptyText: (
              <TableEmptyState
                icon="📜"
                message={date ? `${date.format('YYYY/MM/DD')}엔 반영 기록이 없습니다` : '반영 기록이 없습니다'}
                t={t}
              />
            ),
          }}
        />
      )}
    </DashboardLayout>
  );
};
